import { ChangeEvent, useCallback, useEffect } from 'react';
import { Chip, Icon, Link, Text } from '@nimbus-ds/components';
import { EditIcon } from '@nimbus-ds/icons';
import { ProductRestrictionType } from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { useUpsellProtectedActions } from '../../../../../hooks';
import {
  useAutoResetSelection,
  useProductsForRestrictions,
} from '../../../../ShippingRestrictionPages/hooks';
import { createSyntheticChangeEvent } from '../../../../ShippingRestrictionPages/utils';
import { ProductsSelectionModal } from '../../../SelectionModals';

interface ProductSelectionSectionProps {
  selectedProductIds: string[];
  onProductsChange: (variantIds: string[]) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

function ProductSelectionSection({
  selectedProductIds,
  onProductsChange,
  onChange,
}: Readonly<ProductSelectionSectionProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.restrictions.restrictedProducts',
  );

  const {
    products,
    hasMoreProducts,
    fetchProducts,
    loadMoreProducts,
    isRefreshing,
    isLoading,
  } = useProductsForRestrictions();

  const [showModal, openModal, closeModal] = useModal();

  useEffect(() => {
    if (selectedProductIds.length === 0) {
      openModal();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    handleSubmit: handleAutoResetSubmit,
    handleClose: handleAutoResetClose,
  } = useAutoResetSelection({
    fieldName: 'productRestrictionType',
    resetValue: ProductRestrictionType.NO_RESTRICTION,
    onChange,
    onItemsChange: onProductsChange,
    selectedItems: selectedProductIds,
    allItems: products,
    getItemId: (product) => product.id,
  });

  const { withUpsellCheck } = useUpsellProtectedActions({
    onUpsellDecline: () => handleAutoResetSubmit([]),
  });

  const handleSubmit = useCallback(
    (variantIds: string[]) => {
      handleAutoResetSubmit(variantIds);
      closeModal();
    },
    [handleAutoResetSubmit, closeModal],
  );

  const handleClose = useCallback(() => {
    handleAutoResetClose();
    closeModal();
  }, [handleAutoResetClose, closeModal]);

  const handleRemoveAll = withUpsellCheck(
    useCallback(() => {
      onChange(
        createSyntheticChangeEvent(
          'productRestrictionType',
          ProductRestrictionType.NO_RESTRICTION,
        ),
      );
      onProductsChange([]);
    }, [onChange, onProductsChange]),
  );

  const productsCountText =
    selectedProductIds.length === 1
      ? t('productsSelectedSingle', { count: selectedProductIds.length })
      : t('productsSelectedMultiple', { count: selectedProductIds.length });

  return (
    <>
      {selectedProductIds.length > 0 && (
        <Stack.Item>
          <Stack column align="stretch" spacing="base">
            <Chip
              id="products-count"
              text={productsCountText}
              removable
              onClick={handleRemoveAll}
            />
            <Link
              as="button"
              textDecoration="none"
              onClick={withUpsellCheck(openModal)}
            >
              <Icon color="primary-interactive" source={<EditIcon />} />
              <Text color="primary-interactive">{t('editProducts')}</Text>
            </Link>
          </Stack>
        </Stack.Item>
      )}

      {showModal && (
        <ProductsSelectionModal
          isOpen
          onClose={handleClose}
          onSubmit={handleSubmit}
          preselectedVariantIds={selectedProductIds}
          products={products}
          fetchProducts={fetchProducts}
          loadMoreProducts={loadMoreProducts}
          isLoading={isLoading}
          isLoadingMore={isRefreshing}
          hasMoreProducts={hasMoreProducts}
        />
      )}
    </>
  );
}

export default ProductSelectionSection;
